import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateCustomerRole } from '../../store/slices/customerSlice';
import AiAgentService from '../../api/AiAgentService';
import NotificationSnackbar from '../../pages/AiSupportSettings/components/NotificationSnackbar';
import useFormatDate from '../../hooks/useFormatDate';
import { MdEdit } from "react-icons/md";
import './style.css';

const CustomerChatsList = ({ customer, onChatSelect, activeChat, onRoleUpdate }) => {
    const dispatch = useDispatch();
    const { formatRelativeDate } = useFormatDate();
    const [showEditModal, setShowEditModal] = useState(false);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [selectedRole, setSelectedRole] = useState('');
    const [loadingRoles, setLoadingRoles] = useState(false);
    const [savingRole, setSavingRole] = useState(false);
    
    // Стани для NotificationSnackbar
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    // Функція для показу сповіщень
    const showNotification = (message, severity = 'success') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    const handleEditCustomer = async () => {
        if (!customer) return;
        
        setShowEditModal(true);
        setLoadingRoles(true);
        
        try {
            const response = await AiAgentService.getCustomerRole();
            setAvailableRoles(response.roles || []);
            setSelectedRole(customer.role?.id || '');
        } catch (error) {
            console.error('Error fetching roles:', error);
            setAvailableRoles([]);
        } finally {
            setLoadingRoles(false);
        }
    };

    const handleSaveRole = async () => {
        if (!customer || !selectedRole) return;
        
        setSavingRole(true);
        
        try {
            const response = await AiAgentService.setCustomerRole(customer.id, selectedRole);
            
            if (response.success) {
                setShowEditModal(false);
                
                // Оновлюємо customer в Redux
                if (response.new_role_title) {
                    dispatch(updateCustomerRole({
                        customerId: customer.id,
                        newRoleTitle: response.new_role_title
                    }));
                    
                    // Якщо є callback, викликаємо його для оновлення чатів
                    if (onRoleUpdate) {
                        onRoleUpdate(customer.id, response.new_role_title);
                    }
                }
                
                showNotification(response.message || 'Роль успішно оновлена!', 'success');
            } else {
                showNotification('Помилка при оновленні ролі', 'error');
            }
        } catch (error) {
            console.error('Error updating role:', error);
            const errorMessage = error.response?.data?.error || 'Помилка при оновленні ролі';
            showNotification(errorMessage, 'error');
        } finally {
            setSavingRole(false);
        }
    };

    const handleCloseModal = () => {
        setShowEditModal(false);
        setSelectedRole('');
        setAvailableRoles([]);
    };

    if (!customer) {
        return (
            <div className="customer-chats-sidebar">
                <div className="no-customer-selected">
                    <p>Оберіть користувача для перегляду чатів</p>
                </div>
            </div>
        );
    }

    const chats = customer.chats || [];

    const textForIcon = (chat) => {
        const title = chat?.title || "AI";
        const words = title.split(' ');
        if (words.length > 1) {
            return words[0][0].toUpperCase() + words[1][0].toUpperCase();
        }
        return title[0].toUpperCase();
    };

    return (
        <div className="customer-chats-sidebar">
            <div className="customer-chats-header">
                <div className="customer-header-title">
                    <h3>{customer.customer_name}</h3>
                    <p className='customer-chats-header-role'>({customer.role?.title || 'Немає ролі'})</p>
                    <button 
                        className="edit-customer-btn"
                        onClick={handleEditCustomer}
                        title="Редагувати роль користувача"
                    >
                        <MdEdit size={'18px'} />
                    </button>
                </div>
                <p className="customer-chats-info">
                    {chats.length} {chats.length === 1 ? 'чат' : chats.length < 5 ? 'чати' : 'чатів'}
                </p>
            </div>

            <div className="customer-chats-list">
                {chats.length === 0 ? (
                    <div className="no-chats">
                        <p>У цього користувача немає чатів</p>
                    </div>
                ) : (
                    chats.map((chat) => (
                        <div
                            key={chat.id}
                            className={`customer-chat-item ${activeChat?.id === chat.id ? 'active' : ''}`}
                            onClick={() => onChatSelect(chat)}
                        >
                            <div className="customer-chat-icon">{textForIcon(chat)}</div>
                            <div className="customer-chat-info">
                                <div className="customer-chat-header-row">
                                    <p className="customer-chat-title" title={chat.title}>{chat.title}</p>
                                    {chat.messages_count > 0 && (
                                        <span 
                                            className="messages-badge" 
                                            title={`Кількість повідомлень: ${chat.messages_count}`}
                                        >
                                            {chat.messages_count}
                                        </span>
                                    )}
                                </div>
                                <div className="customer-chat-meta">
                                    <p 
                                        className="customer-chat-date" 
                                        title={new Date(chat.updated_at).toLocaleString('uk-UA')}
                                    >
                                        {formatRelativeDate(chat.updated_at)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
            
            {showEditModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4>Редагування ролі користувача</h4>
                            <button className="close-btn" onClick={handleCloseModal}>×</button>
                        </div>
                        <div className="modal-body">
                            {customer && (
                                <div className="customer-info-modal">
                                    <p><strong>Користувач:</strong> {customer.customer_name}</p>
                                    <p><strong>Поточна роль:</strong> {customer.role?.title || 'N/A'}</p>
                                </div>
                            )}
                            
                            {loadingRoles ? (
                                <div className="loading">Завантаження ролей...</div>
                            ) : (
                                <div className="role-select-container">
                                    <label htmlFor="role-select">Оберіть нову роль:</label>
                                    <select 
                                        id="role-select"
                                        value={selectedRole} 
                                        onChange={(e) => setSelectedRole(e.target.value)}
                                        disabled={savingRole}
                                    >
                                        <option value="">Оберіть роль...</option>
                                        {availableRoles.map((role) => (
                                            <option key={role.id} value={role.id}>
                                                {role.title}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="cancel-btn" 
                                onClick={handleCloseModal}
                                disabled={savingRole}
                            >
                                Скасувати
                            </button>
                            <button 
                                className="save-btn" 
                                onClick={handleSaveRole}
                                disabled={!selectedRole || savingRole || loadingRoles}
                            >
                                {savingRole ? 'Збереження...' : 'Зберегти'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            <NotificationSnackbar
                open={snackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={handleCloseSnackbar}
            />
        </div>
    );
};

export default CustomerChatsList;
